import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DownloadOutlineIcon from 'mdi-react/DownloadOutlineIcon';
import QrcodeIcon from 'mdi-react/QrcodeIcon';
import { QRCodeCanvas } from 'qrcode.react';
import StyleConstants from '../../../core/styles.constants';
import { useWorkplaceQrCode } from '../hooks/use-workplace-qr-code';

interface Props {
  /** The `Workplaces.Id` behind the selected desk, or undefined when the desk has no row yet. */
  readonly workplaceId?: string;
}

/** Big enough to scan off a screen, small enough for the inspector column. */
const QR_PIXELS = 132;

/** Printed at a size that survives a phone camera; 4× the on-screen code. */
const DOWNLOAD_PIXELS = QR_PIXELS * 4;

/**
 * The QR sticker linked to the selected desk.
 *
 * Renders nothing at all for a desk with no sticker. Most desks on most plans have never had
 * one linked, and an empty "no QR code" panel under every desk would be a permanent piece of
 * furniture reporting the absence of a thing nobody was looking for.
 *
 * The code encodes the bare short code — the form the app's scanner documents as the current
 * one (`QrCodePayloadParser`, case 4), and the same characters shown as text beside it, so an
 * admin comparing the screen with a printed sticker is comparing like with like. It is
 * deliberately not the deep link: a code that resolves only on a device with the app
 * installed reads as broken everywhere else.
 */
const WorkplaceQrCode = ({ workplaceId }: Props): React.JSX.Element | null => {
  const { t } = useTranslation();
  const { data, error } = useWorkplaceQrCode(workplaceId);
  // Two canvases: the visible one, and a larger off-screen one the download reads. Saving
  // the on-screen code would produce a PNG that takes three attempts to scan on paper.
  const printable = useRef<HTMLDivElement>(null);

  /**
   * Saves the code as a PNG, re-rendered larger than the one on screen.
   *
   * Reads the canvas the code is already drawn on rather than asking the server for the
   * printable PDF: that endpoint generates a fresh batch of unlinked codes, which would hand
   * the admin a different sticker from the one on this desk.
   */
  const download = (): void => {
    const canvas = printable.current?.querySelector('canvas');

    if (!canvas || !data?.shortCode) {
      return;
    }

    const link = window.document.createElement('a');

    link.href = canvas.toDataURL('image/png');
    link.download = `qr-${data.shortCode}.png`;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  if (error === 'failed' || error === 'forbidden') {
    return <Alert severity="warning" sx={{ mt: 2 }}>{t('floorplan-editor-qr-code-failed')}</Alert>;
  }

  // A desk with no sticker, a desk whose workplace does not exist yet, and a request still in
  // flight all show nothing: there is no QR code to talk about in any of them.
  if (!data?.shortCode) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 2,
        borderColor: StyleConstants.LIGHT_GREY_COLOR,
        backgroundColor: StyleConstants.WHITE_COLOR,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }}>
        <QrcodeIcon size={16} color={StyleConstants.DARK_BLUE_COLOR} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}
          color={StyleConstants.DARK_BLUE_COLOR}
        >
          {t('floorplan-editor-qr-code-title')}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* The code sits on its own white tile with a hairline border: a QR on a tinted or
            edge-to-edge surface is one some scanners refuse, and the frame makes it read as an
            object to point a phone at rather than as decoration. */}
        <Box
          sx={{
            p: 0.75,
            flexShrink: 0,
            lineHeight: 0,
            borderRadius: 1.5,
            border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
            backgroundColor: StyleConstants.WHITE_COLOR,
          }}
        >
          <QRCodeCanvas
            value={data.shortCode}
            size={QR_PIXELS}
            level="Q"
            includeMargin={false}
            title={String(t('floorplan-editor-qr-code-image-title', { code: data.shortCode }))}
          />
        </Box>

        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Chip
            label={data.shortCode}
            size="small"
            variant="outlined"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: 1.5,
              borderRadius: 1,
              borderColor: StyleConstants.LIGHT_GREY_COLOR,
            }}
          />

          <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
            {t('floorplan-editor-qr-code-hint')}
          </Typography>

          {/* A small labelled button rather than a full-width one: the inspector is a narrow
              column, and a button as wide as the panel would outweigh the code it belongs to. */}
          <Button
            size="small"
            variant="text"
            onClick={download}
            startIcon={<DownloadOutlineIcon size={16} />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {t('floorplan-editor-qr-code-download')}
          </Button>
        </Stack>
      </Stack>

      <Box
        ref={printable}
        sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
        aria-hidden
      >
        <QRCodeCanvas value={data.shortCode} size={DOWNLOAD_PIXELS} level="Q" includeMargin />
      </Box>
    </Paper>
  );
};

export default WorkplaceQrCode;
